import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import url from '../constants/config';
import ConfirmModal from '../shared/displayComponents/confirmModal/ConfirmModal';
import Loader from '../shared/displayComponents/loader/Loader';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const PRIORITY_LABELS = {
  high: { label: 'Wysoki', bg: '#fee2e2', color: '#dc2626' },
  medium: { label: 'Średni', bg: '#fef3c7', color: '#d97706' },
  low: { label: 'Niski', bg: '#f0fdf4', color: '#16a34a' },
};

const STATUS_LABELS = {
  not_started: { label: 'Do zrobienia', bg: '#f1f5f9', color: '#475569' },
  in_progress: { label: 'W trakcie', bg: '#dbeafe', color: '#1d4ed8' },
  waiting: { label: 'Oczekuje', bg: '#fef3c7', color: '#b45309' },
  done: { label: 'Gotowe', bg: '#dcfce7', color: '#15803d' },
};

const AdminAssistants = () => {
  const history = useHistory();
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [editingRate, setEditingRate] = useState({});
  const [rateInputs, setRateInputs] = useState({});
  const [saving, setSaving] = useState({});
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, confirmLabel: 'Potwierdź' });
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, show: false }));
  const [deletingEmail, setDeletingEmail] = useState(null);

  /* Global tasks */
  const [globalTasks, setGlobalTasks] = useState([]);
  const [gtLoading, setGtLoading] = useState(true);
  const [gtForm, setGtForm] = useState({ label: '', assignedToAssistant: '', priority: '', deadline: '' });
  const [gtSaving, setGtSaving] = useState(false);
  const [gtError, setGtError] = useState('');
  const [gtDeletingId, setGtDeletingId] = useState(null);

  useEffect(() => {
    axios
      .get(`${url}/admin/assistants`, { headers: getAuthHeader() })
      .then(res => setAssistants(res.data.assistants || []))
      .catch(() => setError('Nie udało się załadować asystentów.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    axios
      .get(`${url}/checklist/global-tasks`, { headers: getAuthHeader() })
      .then(res => setGlobalTasks(res.data.tasks || []))
      .catch(() => {})
      .finally(() => setGtLoading(false));
  }, []);

  const handleGtSubmit = async (e) => {
    e.preventDefault();
    if (!gtForm.label.trim()) return;
    setGtSaving(true);
    setGtError('');
    try {
      const res = await axios.post(
        `${url}/checklist/global-tasks`,
        {
          label: gtForm.label.trim(),
          assignedToAssistant: gtForm.assignedToAssistant || null,
          priority: gtForm.priority || null,
          deadline: gtForm.deadline || null,
        },
        { headers: getAuthHeader() }
      );
      setGlobalTasks(prev => [res.data.task, ...prev]);
      setGtForm({ label: '', assignedToAssistant: '', priority: '', deadline: '' });
    } catch {
      setGtError('Nie udało się zapisać zadania.');
    } finally {
      setGtSaving(false);
    }
  };

  const handleGtStatusChange = async (taskId, newStatus) => {
    setGlobalTasks(prev => prev.map(t => t._id === taskId ? { ...t, status: newStatus } : t));
    try {
      await axios.patch(`${url}/checklist/global-tasks/${taskId}`, { status: newStatus }, { headers: getAuthHeader() });
    } catch { /* revert */ axios.get(`${url}/checklist/global-tasks`, { headers: getAuthHeader() }).then(res => setGlobalTasks(res.data.tasks || [])); }
  };

  const handleGtDelete = async (taskId) => {
    setGtDeletingId(taskId);
    try {
      await axios.delete(`${url}/checklist/global-tasks/${taskId}`, { headers: getAuthHeader() });
      setGlobalTasks(prev => prev.filter(t => t._id !== taskId));
    } catch { /* noop */ }
    finally { setGtDeletingId(null); }
  };

  const toggleExpand = email => {
    setExpanded(prev => ({ ...prev, [email]: !prev[email] }));
  };

  const startEditRate = (email, currentRate) => {
    setEditingRate(prev => ({ ...prev, [email]: true }));
    setRateInputs(prev => ({ ...prev, [email]: currentRate != null ? String(currentRate) : '' }));
  };

  const cancelEditRate = email => {
    setEditingRate(prev => ({ ...prev, [email]: false }));
  };

  const saveRate = async email => {
    const val = rateInputs[email];
    if (val === '' || isNaN(Number(val))) return;
    setSaving(prev => ({ ...prev, [email]: true }));
    try {
      await axios.put(
        `${url}/admin/assistants/${encodeURIComponent(email)}/rate`,
        { hourlyRate: Number(val) },
        { headers: getAuthHeader() }
      );
      setAssistants(prev =>
        prev.map(a =>
          a.universityEmail === email ? { ...a, hourlyRate: Number(val) } : a
        )
      );
      setEditingRate(prev => ({ ...prev, [email]: false }));
    } catch {
      // ignore
    } finally {
      setSaving(prev => ({ ...prev, [email]: false }));
    }
  };

  const revokeAccess = (playerId, assistantEmail) => {
    setConfirmModal({
      show: true,
      title: 'Cofnąć dostęp?',
      message: 'Asystent straci dostęp do tego zawodnika.',
      onConfirm: async () => {
        closeConfirm();
        try {
          await axios.delete(
            `${url}/admin/players/${playerId}/full-access/${encodeURIComponent(assistantEmail)}`,
            { headers: getAuthHeader() }
          );
          setAssistants(prev =>
            prev.map(a =>
              a.universityEmail === assistantEmail
                ? {
                    ...a,
                    assignedPlayers: a.assignedPlayers.filter(p => p.playerId !== playerId),
                    assignedCount: a.assignedCount - 1,
                  }
                : a
            )
          );
        } catch {
          // ignore
        }
      },
    });
  };

  const deleteAssistant = (assistantEmail, name) => {
    setConfirmModal({
      show: true,
      title: 'Usunąć asystenta?',
      message: `Asystent ${name} zostanie trwale usunięty z systemu. Wszystkie przypisania do zawodników zostaną usunięte, a taski odpięte. Tej operacji nie można cofnąć.`,
      confirmLabel: 'Usuń asystenta',
      onConfirm: async () => {
        closeConfirm();
        setDeletingEmail(assistantEmail);
        try {
          await axios.delete(
            `${url}/admin/assistants/${encodeURIComponent(assistantEmail)}`,
            { headers: getAuthHeader() }
          );
          setAssistants(prev => prev.filter(a => a.universityEmail !== assistantEmail));
        } catch {
          // ignore
        } finally {
          setDeletingEmail(null);
        }
      },
    });
  };

  const totalAssigned = assistants.reduce((s, a) => s + a.assignedCount, 0);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div style={{ padding: '32px', color: '#ef4444' }}>{error}</div>;
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: '900px' }}>
      <h2 style={{ margin: '0 0 4px', color: '#1e293b', fontSize: '22px', fontWeight: 700 }}>
        Zarządzanie Asystentami
      </h2>
      <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: '14px' }}>
        {assistants.length} asystent{assistants.length !== 1 ? 'ów' : ''} &mdash; {totalAssigned} przypisanych zawodników
      </p>

      <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
        {/* Table header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 160px 100px 150px 40px',
            padding: '10px 16px',
            background: '#f8fafc',
            borderBottom: '2px solid #e2e8f0',
            fontSize: '12px',
            fontWeight: 600,
            color: '#475569',
          }}
        >
          <span>Asystent</span>
          <span>Stawka/h (PLN)</span>
          <span>Zawodnicy</span>
          <span />
        </div>

        {assistants.length === 0 && (
          <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>
            Brak asystentów.
          </div>
        )}

        {assistants.map(a => {
          const email = a.universityEmail;
          const isExpanded = expanded[email];
          const isEditing = editingRate[email];
          const isSaving = saving[email];

          return (
            <div key={email} style={{ borderBottom: '1px solid #f1f5f9' }}>
              {/* Main row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 160px 100px 150px 40px',
                  padding: '12px 16px',
                  alignItems: 'center',
                  background: isExpanded ? '#f8fafc' : '#fff',
                }}
              >
                {/* Name + email */}
                <div>
                  <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>
                    {a.firstName} {a.lastName}
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '12px' }}>{email}</div>
                </div>

                {/* Rate */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isEditing ? (
                    <>
                      <input
                        type="number"
                        value={rateInputs[email] || ''}
                        onChange={e => {
                          const { value } = e.target;
                          setRateInputs(prev => ({ ...prev, [email]: value }));
                        }}
                        min="0"
                        step="0.01"
                        style={{
                          width: '80px',
                          padding: '4px 8px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '13px',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => saveRate(email)}
                        disabled={isSaving}
                        style={{
                          padding: '4px 8px',
                          background: '#16a34a',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '5px',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        {isSaving ? '...' : 'Zapisz'}
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelEditRate(email)}
                        style={{
                          padding: '4px 8px',
                          background: '#f1f5f9',
                          color: '#475569',
                          border: 'none',
                          borderRadius: '5px',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        Anuluj
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '14px', color: a.hourlyRate != null ? '#1e293b' : '#94a3b8' }}>
                        {a.hourlyRate != null ? `${a.hourlyRate} PLN` : '—'}
                      </span>
                      <button
                        type="button"
                        onClick={() => startEditRate(email, a.hourlyRate)}
                        title="Edytuj stawkę"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#64748b',
                          padding: '2px 4px',
                          fontSize: '12px',
                        }}
                      >
                        <i className="fas fa-pencil-alt" />
                      </button>
                    </>
                  )}
                </div>

                {/* Assigned count */}
                <div style={{ fontSize: '14px', color: '#334155' }}>
                  {a.assignedCount > 0 ? (
                    <span
                      style={{
                        background: '#e0f2fe',
                        color: '#0369a1',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {a.assignedCount}
                    </span>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>0</span>
                  )}
                </div>

                {/* Details button */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    type="button"
                    onClick={() => history.push(`/admin/assistants/${encodeURIComponent(email)}`)}
                    style={{
                      padding: '5px 12px',
                      background: '#2e496b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Szczegóły →
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteAssistant(email, `${a.firstName} ${a.lastName}`)}
                    disabled={deletingEmail === email}
                    title="Usuń asystenta"
                    style={{
                      padding: '5px 8px',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: deletingEmail === email ? 'not-allowed' : 'pointer',
                      opacity: deletingEmail === email ? 0.6 : 1,
                    }}
                  >
                    <i className="fas fa-trash-alt" />
                  </button>
                </div>

                {/* Expand toggle */}
                <button
                  type="button"
                  onClick={() => toggleExpand(email)}
                  disabled={a.assignedCount === 0}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: a.assignedCount > 0 ? 'pointer' : 'default',
                    color: a.assignedCount > 0 ? '#64748b' : '#cbd5e1',
                    fontSize: '14px',
                  }}
                >
                  <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
                </button>
              </div>

              {/* Expanded: assigned players */}
              {isExpanded && a.assignedPlayers.length > 0 && (
                <div style={{ background: '#f8fafc', padding: '4px 16px 12px 40px' }}>
                  {a.assignedPlayers.map(p => (
                    <div
                      key={p.playerId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 0',
                        borderBottom: '1px solid #e2e8f0',
                        fontSize: '13px',
                      }}
                    >
                      <span style={{ color: '#334155' }}>
                        <i className="fas fa-user" style={{ marginRight: '6px', color: '#94a3b8', fontSize: '11px' }} />
                        {p.playerName}
                      </span>
                      <button
                        type="button"
                        onClick={() => revokeAccess(p.playerId, email)}
                        style={{
                          padding: '3px 8px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '5px',
                          fontSize: '11px',
                          cursor: 'pointer',
                        }}
                      >
                        Cofnij dostęp
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Global Tasks Section ── */}
      <div style={{ marginTop: '40px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
          <i className="fas fa-clipboard-list" style={{ marginRight: '8px', color: '#2e496b' }} />
          Zadania globalne
        </h3>
        <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '13px' }}>
          Zadania niezwiązane z konkretnym zawodnikiem — widoczne u przypisanego asystenta na Kanbanie.
        </p>

        {/* Add task form */}
        <form
          onSubmit={handleGtSubmit}
          style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '16px 20px',
            marginBottom: '20px',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b', marginBottom: '14px' }}>
            + Nowe zadanie globalne
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px 160px', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Nazwa zadania *</label>
              <input
                type="text"
                value={gtForm.label}
                onChange={e => { const v = e.target.value; setGtForm(f => ({ ...f, label: v })); }}
                placeholder="Np. Przygotuj raport miesięczny"
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Asystent</label>
              <select
                value={gtForm.assignedToAssistant}
                onChange={e => { const v = e.target.value; setGtForm(f => ({ ...f, assignedToAssistant: v })); }}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }}
              >
                <option value="">-- bez przypisania --</option>
                {assistants.map(a => (
                  <option key={a.universityEmail} value={a.universityEmail}>
                    {a.firstName} {a.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Priorytet</label>
              <select
                value={gtForm.priority}
                onChange={e => { const v = e.target.value; setGtForm(f => ({ ...f, priority: v })); }}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }}
              >
                <option value="">brak</option>
                <option value="high">Wysoki</option>
                <option value="medium">Średni</option>
                <option value="low">Niski</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Termin</label>
              <input
                type="date"
                value={gtForm.deadline}
                onChange={e => { const v = e.target.value; setGtForm(f => ({ ...f, deadline: v })); }}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          {gtError && <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '8px' }}>{gtError}</div>}
          <button
            type="submit"
            disabled={gtSaving || !gtForm.label.trim()}
            style={{
              padding: '8px 20px',
              background: gtSaving || !gtForm.label.trim() ? '#e2e8f0' : '#2e496b',
              color: gtSaving || !gtForm.label.trim() ? '#94a3b8' : '#fff',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: gtSaving || !gtForm.label.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {gtSaving ? 'Zapisywanie...' : 'Dodaj zadanie'}
          </button>
        </form>

        {/* Task list */}
        {gtLoading ? (
          <div style={{ color: '#94a3b8', fontSize: '14px' }}>Ładowanie...</div>
        ) : globalTasks.length === 0 ? (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
            Brak globalnych zadań.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {globalTasks.map(task => {
              const priorityMeta = PRIORITY_LABELS[task.priority];
              const statusMeta = STATUS_LABELS[task.status] || STATUS_LABELS.not_started;
              const assignedAssistant = assistants.find(a => a.universityEmail === task.assignedToAssistant);
              return (
                <div
                  key={task._id}
                  style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Status badge (clickable cycle) */}
                  <button
                    type="button"
                    title="Zmień status"
                    onClick={() => {
                      const cycle = ['not_started', 'in_progress', 'waiting', 'done'];
                      const next = cycle[(cycle.indexOf(task.status) + 1) % cycle.length];
                      handleGtStatusChange(task._id, next);
                    }}
                    style={{
                      background: statusMeta.bg,
                      color: statusMeta.color,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '3px 10px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    {statusMeta.label}
                  </button>

                  {/* Label */}
                  <div style={{ flex: 1, fontSize: '14px', color: '#1e293b', fontWeight: 500, minWidth: '120px' }}>
                    {task.label}
                  </div>

                  {/* Assigned */}
                  <div style={{ fontSize: '12px', color: '#64748b', flexShrink: 0 }}>
                    {assignedAssistant
                      ? <><i className="fas fa-user" style={{ marginRight: '4px' }} />{assignedAssistant.firstName} {assignedAssistant.lastName}</>
                      : <span style={{ color: '#cbd5e1' }}>nieprzypisane</span>
                    }
                  </div>

                  {/* Priority */}
                  {priorityMeta && (
                    <span style={{ background: priorityMeta.bg, color: priorityMeta.color, borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: 600, flexShrink: 0 }}>
                      {priorityMeta.label}
                    </span>
                  )}

                  {/* Deadline */}
                  {task.deadline && (
                    <span style={{ fontSize: '12px', color: '#6366f1', flexShrink: 0 }}>
                      <span role="img" aria-label="termin">⏰</span> {new Date(task.deadline).toLocaleDateString('pl-PL')}
                    </span>
                  )}

                  {/* Delete */}
                  <button
                    type="button"
                    onClick={() => handleGtDelete(task._id)}
                    disabled={gtDeletingId === task._id}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '14px', padding: '2px 4px', flexShrink: 0 }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; }}
                  >
                    <i className="fas fa-trash-alt" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm || (() => {})}
        onCancel={closeConfirm}
        confirmLabel={confirmModal.confirmLabel || 'Potwierdź'}
        danger
      />
    </div>
  );
};

export default AdminAssistants;
